import { useEffect, useMemo, useState, useCallback } from "react";
import DataTable from "react-data-table-component";
import Container from "react-bootstrap/esm/Container";
import Button from "react-bootstrap/Button";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import { IconButton } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import RefreshIcon from "@mui/icons-material/Refresh";

import { API } from "../api/API";
import { alertWarning } from "../alert";

export default function PendingReport() {
  const api = useMemo(() => new API(), []);
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");

  const [filters, setFilters] = useState({
    salesOrderNumber: "",
    createdBy: "",
    createdAt: "",
  });

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await api.getPendingReport();
      setData(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching Pending Report:", error);
      alertWarning("Failed to fetch Pending Report.");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const filterData = () => {
    if (startDate && endDate && startDate > endDate) {
      alertWarning("Start date can't be after end date.");
      return;
    }

    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
  };

  const resetPage = () => {
    setStartDate("");
    setEndDate("");
    setAppliedStartDate("");
    setAppliedEndDate("");

    setFilters({
      salesOrderNumber: "",
      createdBy: "",
      createdAt: "",
    });

    loadData();
  };

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      const soNumber = String(row.sales_order_number || "").toLowerCase();
      const createdBy = String(row.created_by || "").toLowerCase();
      const createdAt = String(row.created_at || "");
      const createdDate = createdAt.slice(0, 10);

      return (
        soNumber.includes(filters.salesOrderNumber.toLowerCase()) &&
        createdBy.includes(filters.createdBy.toLowerCase()) &&
        createdAt.toLowerCase().includes(filters.createdAt.toLowerCase()) &&
        (!appliedStartDate ||
          (createdDate && createdDate >= appliedStartDate)) &&
        (!appliedEndDate || (createdDate && createdDate <= appliedEndDate))
      );
    });
  }, [data, filters, appliedStartDate, appliedEndDate]);

  const mappedData = filteredData.map((row, index) => ({
    "S.No.": index + 1,
    "SO #": row.sales_order_number || "",
    "Created By": row.created_by || "",
    "Created At": row.created_at || "",
  }));

  const handleExport = () => {
    if (!mappedData.length) {
      alertWarning("No data available to export.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(mappedData);
    worksheet["!cols"] = [{ wch: 10 }, { wch: 25 }, { wch: 20 }, { wch: 22 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pending Report");
    XLSX.writeFile(workbook, "Pending_Report.xlsx");
  };

  const headerInputStyle = {
    display: "block",
    marginTop: "4px",
    marginBottom: "0",
    width: "100%",
    minWidth: 0,
    height: "30px",
    padding: "4px 6px",
    boxSizing: "border-box",
    textAlign: "center",
    border: "1px solid #ced4da",
    borderRadius: "3px",
    fontSize: "13px",
  };

  const headerContainerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "center",
    width: "100%",
    minWidth: 0,
    padding: "2px 5px",
    boxSizing: "border-box",
  };

  const columns = [
    {
      name: (
        <div style={headerContainerStyle}>
          <span style={{ textAlign: "center" }}>S.No.</span>
          {/* <input
            type="text"
            placeholder="S.No."
            disabled
            style={headerInputStyle}
          /> */}
        </div>
      ),
      selector: (row, index) => index + 1,
      sortable: false,
      center: true,
      grow: 0,
      width: "75px",
      minWidth: "75px",
      maxWidth: "75px",
    },
    {
      name: (
        <div style={headerContainerStyle}>
          <span style={{ textAlign: "center" }}>SO #</span>
          <input
            type="text"
            name="salesOrderNumber"
            value={filters.salesOrderNumber}
            onChange={handleFilterChange}
            placeholder="SO #"
            style={headerInputStyle}
          />
        </div>
      ),
      selector: (row) => row.sales_order_number || "",
      sortable: true,
      center: true,
      grow: 2,
      minWidth: "200px",
      cell: (row) => (
        <div
          title={row.sales_order_number || ""}
          style={{
            width: "100%",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            padding: "10px 8px",
            boxSizing: "border-box",
            textAlign: "center",
          }}
        >
          <span
            onClick={() => navigate(`/pending-report/${row.id}`)}
            style={{
              color: "#0d6efd",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {row.sales_order_number || ""}
          </span>
        </div>
      ),
    },
    {
      name: (
        <div style={headerContainerStyle}>
          <span style={{ textAlign: "center" }}>Created By</span>
          <input
            type="text"
            name="createdBy"
            value={filters.createdBy}
            onChange={handleFilterChange}
            placeholder="Created By"
            style={headerInputStyle}
          />
        </div>
      ),
      selector: (row) => row.created_by || "",
      sortable: true,
      center: true,
      grow: 2,
      minWidth: "200px",
    },
    {
      name: (
        <div style={headerContainerStyle}>
          <span style={{ textAlign: "center" }}>Created At</span>
          <input
            type="text"
            name="createdAt"
            value={filters.createdAt}
            onChange={handleFilterChange}
            placeholder="Created At"
            style={headerInputStyle}
          />
        </div>
      ),
      selector: (row) => row.created_at || "",
      sortable: true,
      center: true,
      grow: 2,
      minWidth: "220px",
    },
  ];

  const customStyles = {
    table: {
      style: {
        width: "100%",
        minWidth: "100%",
      },
    },
    tableWrapper: {
      style: {
        width: "100%",
        minWidth: "100%",
      },
    },
    head: {
      style: {
        backgroundColor: "#243847",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#243847",
        borderBottomWidth: "1px",
        borderBottomColor: "#ffffff",
        minHeight: "68px",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#243847",
        color: "#ffffff",
        fontSize: "15px",
        fontWeight: "700",
        paddingLeft: "4px",
        paddingRight: "4px",
        whiteSpace: "normal",
      },
    },
    rows: {
      style: {
        minHeight: "42px",
        fontSize: "15px",
      },
    },
    cells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
        minWidth: 0,
        overflow: "hidden",
      },
    },
  };

  return (
    <>
      <div
        className="basic"
        style={{
          position: "relative",
          marginTop: "30px",
        }}
      />

      <Container
        fluid
        style={{
          width: "100%",
          maxWidth: "100%",
          margin: 0,
          padding: "0 16px",
          boxSizing: "border-box",
        }}
      >
        {/* Heading */}
        <div
          style={{
            width: "100%",
            textAlign: "center",
            marginTop: "35px",
            marginBottom: "30px",
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: "22px",
              fontWeight: "700",
              color: "#243847",
            }}
          >
            Pending Report
          </h4>
        </div>

        {/* Toolbar */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            width: "100%",
            gap: "12px",
            marginBottom: "24px",
          }}
        >
          {/* Excel */}
          <div style={{ justifySelf: "start" }}>
            <Tooltip title="Download Excel" placement="top">
              <span>
                <IconButton
                  onClick={handleExport}
                  disabled={loading || filteredData.length === 0}
                  aria-label="Download Excel"
                >
                  <FontAwesomeIcon
                    icon={faFileExcel}
                    style={{
                      width: "27px",
                      height: "27px",
                      color: "#243847",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </div>

          {/* Date filters */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                margin: 0,
                whiteSpace: "nowrap",
              }}
            >
              <span>Start Date</span>
              <input
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  height: "40px",
                  width: "205px",
                  padding: "5px 8px",
                  border: "1px solid #ced4da",
                  borderRadius: "5px",
                  boxSizing: "border-box",
                }}
              />
            </label>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                margin: 0,
                whiteSpace: "nowrap",
              }}
            >
              <span>End Date</span>
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  height: "40px",
                  width: "205px",
                  padding: "5px 8px",
                  border: "1px solid #ced4da",
                  borderRadius: "5px",
                  boxSizing: "border-box",
                }}
              />
            </label>

            <Button
              onClick={filterData}
              style={{
                backgroundColor: "#243847",
                borderColor: "#243847",
                height: "40px",
                padding: "0 18px",
                fontSize: "14px",
              }}
            >
              Search
            </Button>

            {(startDate || endDate || appliedStartDate || appliedEndDate) && (
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  setAppliedStartDate("");
                  setAppliedEndDate("");
                }}
                style={{
                  height: "40px",
                  padding: "0 14px",
                  fontSize: "14px",
                }}
              >
                Clear
              </Button>
            )}
          </div>

          {/* Refresh */}
          <div style={{ justifySelf: "end" }}>
            <Tooltip title="Refresh" placement="top">
              <span>
                <IconButton
                  onClick={resetPage}
                  disabled={loading}
                  aria-label="Refresh report"
                >
                  <RefreshIcon
                    style={{
                      fontSize: "30px",
                      color: "#243847",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>

        {/* Full-width table */}
        <div
          style={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
          }}
        >
          <DataTable
            className="data-table-container"
            columns={columns}
            data={filteredData}
            keyField="id"
            pagination
            responsive
            highlightOnHover
            dense
            progressPending={loading}
            customStyles={customStyles}
            noDataComponent={
              <div style={{ padding: "20px", textAlign: "center" }}>
                No Pending Report records found.
              </div>
            }
          />
        </div>
      </Container>
    </>
  );
}
